/**
 * Entry into a video consultation, shared by the Home / Appointments screens of both portals and by the App
 * ("try again" in the modal, "join" in the presence toast). The order matters: camera and microphone are probed
 * first, inside the click (Safari only grants media from a user gesture), the API is only asked for a token when
 * at least the microphone is available, and the modal opens as "connecting" (never as "in the consultation").
 */

import { setConsultationPresence, setRoom, setVideoSettings } from "store/actions";
import { API, Endpoints } from "utils/api";

import Strings from "utils/strings";
import { notification } from "antd";

export interface MediaProbe {
	ok: boolean;
	video: boolean;
	audio: boolean;
}

export const isIosBrowser = () =>
	/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const stopStream = (stream: MediaStream) => stream.getTracks().forEach((track) => track.stop());

// Camera + microphone first, microphone alone as fallback. The probe tracks are released right away: Twilio creates
// and owns the tracks it publishes.
export const probeMediaPermissions = async (): Promise<MediaProbe> => {
	const devices = navigator.mediaDevices;
	if (!devices?.getUserMedia) return { ok: false, video: false, audio: false };

	try {
		stopStream(await devices.getUserMedia({ audio: true, video: true }));
		return { ok: true, video: true, audio: true };
	} catch (err) {
		// no camera, or camera denied: try the microphone alone
	}

	try {
		stopStream(await devices.getUserMedia({ audio: true }));
		return { ok: true, video: false, audio: true };
	} catch (err) {
		return { ok: false, video: false, audio: false };
	}
};

export const notifyMediaBlocked = () =>
	notification.error({
		message: Strings.video.permissionsTitle,
		description: `${Strings.video.permissionsDenied} ${
			isIosBrowser() ? Strings.video.permissionsHintIos : Strings.video.permissionsHintDesktop
		}`,
		placement: "bottomRight",
		duration: 10
	});

interface StartConsultationVideo {
	id: string;
	// The other party as the screen has it (name / externalName / photo): shown in the modal and in the rating
	counterpart: any;
	source: "psyc" | "web";
	dispatch: (action: any) => void;
	// "Try again" from the modal: the previous room/settings are replaced by the new GET
	retry?: boolean;
}

export const startConsultationVideo = async ({ id, counterpart, source, dispatch, retry = false }: StartConsultationVideo) => {
	const media = await probeMediaPermissions();
	if (!media.ok) {
		notifyMediaBlocked();
		return false;
	}
	if (!media.video) {
		notification.warn({
			message: Strings.video.permissionsTitle,
			description: Strings.video.audioOnly,
			placement: "bottomRight",
			duration: 8
		});
	}

	const response = await API.get({
		url: Endpoints.uriVideoCalls(`consultation/${id}`)
	});

	if (!response.ok) {
		notification.error({
			message: Strings.serverErrors.title,
			description: response?.data?.message || Strings.serverErrors.requestNotPossible,
			placement: "bottomRight",
			duration: 5
		});
		return false;
	}

	const { twilioRoom, userToken, presence } = response.data.results;

	// Initial state of the "<name> is in the room" badges; the socket keeps it current afterwards
	const otherSide = source === "psyc" ? "user" : "psychologist";
	if (presence?.[otherSide]) {
		dispatch(
			setConsultationPresence({
				consultationId: id,
				connected: Boolean(presence[otherSide].connected),
				at: presence[otherSide].since || null,
				name: counterpart?.externalName || counterpart?.name
			})
		);
	}

	const room = { ...twilioRoom, user: counterpart };
	dispatch(setRoom(room));
	dispatch(
		setVideoSettings({
			token: userToken,
			activeVideo: true,
			room,
			appointmentId: id,
			counterpart,
			source,
			media,
			status: "connecting",
			retried: retry
		})
	);

	return true;
};
